import React, { memo, useMemo } from 'react';
import { useBreakpoints } from '@mafc/hooks';

import { isExisty } from 'utils/helpers.util';
import { MarketPlaceDeliveryCostType } from 'network/response/ICart';
import useShipments from 'services/shipments/hooks/useShipments.hook';
import useStepsProgress from 'pages/Checkout/hooks/useStepsProgress.hook';
import useSetShipmentStatus from '../Shipments/hooks/useSetShipmentStatus.hook';
import useDeliverySlot from 'common/DeliverySlot/hooks/useDeliverySlot.hook';

import DynamicLoader from 'routes/DynamicLoader';

import ExpressShipment from '../Common/Shipments/ExpressShipment';
import useShowShipments from './hooks/useShowShipments.hook';
import QCommShipment from '../Common/Shipments/QCommShipment';
import QelecShipment from '../Common/Shipments/QelecShipment';
import { SHIPMENT_CHECKOUT_TYPE } from 'pages/Checkout/constants/checkout.constant';

const ClickNCollect = DynamicLoader(() =>
  import(/* webpackChunkName: "ClickNCollectShipment" */ '../Common/Shipments/ClickNCollect')
) as any;

const ShipmentFood = DynamicLoader(() =>
  import(/* webpackChunkName: "ShipmentFood" */ './ShipmentFood')
) as any;

const MarketPlace = DynamicLoader(() =>
  import(/* webpackChunkName: "MarketPlaceShipment" */ '../Common/Shipments/MarketPlace')
) as any;

const StandardFood = DynamicLoader(() =>
  import(/* webpackChunkName: "foodshipment" */ '../Common/Shipments/StandardFood')
) as any;

const NonFood = DynamicLoader(() =>
  import(/* webpackChunkName: "nonfoodshipment" */ '../Common/Shipments/NonFood')
) as any;

const ShipmentHeading = DynamicLoader(() =>
  import(/* webpackChunkName: "ShipmentHeading" */ '../Common/Shipments/ShipmentHeading')
) as any;
interface Props {
  marketPlaceDeliveryCost: MarketPlaceDeliveryCostType;
  isCnc: boolean;
}

const ShipmentsNew: React.FC<Props> = ({ marketPlaceDeliveryCost, isCnc = false }) => {
  const device = useBreakpoints();
  const [deliverySlot] = useDeliverySlot();
  const shipmentsHook = useShipments();
  const {
    hasClickAndCollect,
    hasExpress,
    hasShipmentFood,
    hasStandardFood,
    hasStandardNonFood,
    hasMarketplace,
    totalShipmentCount,
  } = shipmentsHook;

  const [{ shipments }, { checkIfEnabled, setStepShipmentsStatus }] = useStepsProgress();

  const isEnabled = useMemo(() => checkIfEnabled(true, shipments), [shipments, checkIfEnabled]);

  let isSlotRequired = false;
  let dynamicStatus = false;
  if (isCnc) {
    isSlotRequired = hasShipmentFood;
    dynamicStatus =
      hasExpress || // Don't change this, else place order will not be enabled on express shipments on page reload
      (deliverySlot.slotValidationRequired && isExisty(deliverySlot.selectedSlot)) ||
      !deliverySlot.slotValidationRequired;
  } else {
    isSlotRequired = hasStandardFood && deliverySlot.slotValidationRequired;
    dynamicStatus = isExisty(deliverySlot.selectedSlot);
  }

  useSetShipmentStatus(shipments, isSlotRequired, dynamicStatus, setStepShipmentsStatus);

  const isShowShipment = useMemo(() => isEnabled || device.isMobile, [isEnabled, device.isMobile]);

  const availableShipmentType = useShowShipments();

  return (
    <>
      {!!totalShipmentCount && <ShipmentHeading shipmentCount={totalShipmentCount} />}
      {availableShipmentType[SHIPMENT_CHECKOUT_TYPE.QCOMM] ? (
        <QCommShipment shipmentsHook={shipmentsHook} />
      ) : null}
      {availableShipmentType[SHIPMENT_CHECKOUT_TYPE.QELEC] ? (
        <QelecShipment shipmentsHook={shipmentsHook} />
      ) : null}
      {availableShipmentType[SHIPMENT_CHECKOUT_TYPE.EXPRESS] ? (
        <ExpressShipment shipmentsHook={shipmentsHook} />
      ) : null}
      {hasClickAndCollect && <ClickNCollect shipmentsHook={shipmentsHook} />}
      {(hasStandardFood || hasExpress) && availableShipmentType[SHIPMENT_CHECKOUT_TYPE.SLOTTED] && (
        <StandardFood shipmentsHook={shipmentsHook} isCnc={isCnc} />
      )}
      {hasStandardNonFood && availableShipmentType[SHIPMENT_CHECKOUT_TYPE.STANDARD_NONFOOD] && (
        <NonFood shipmentsHook={shipmentsHook} />
      )}
      {hasMarketplace && (
        <MarketPlace
          marketPlaceDeliveryCost={marketPlaceDeliveryCost}
          shipmentsHook={shipmentsHook}
        />
      )}
      {/* only for cnc drawer */}
      {hasShipmentFood && isShowShipment && (
        <ShipmentFood shipmentsHook={shipmentsHook} hasClickAndCollect={hasClickAndCollect} />
      )}
    </>
  );
};

export default memo(ShipmentsNew);
